import { memo, useState, useEffect, useRef } from 'react'
import type { FC } from 'react'
import { Box, createStyles, keyframes } from '@mantine/core'

const slideRight = keyframes({
  '0%': { left: '-40%' },
  '100%': { left: '100%' },
})

const useStyles = createStyles((theme) => ({
  root: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    height: 4,
    backgroundColor: theme.colors.gray[3],
    overflow: 'hidden',
    zIndex: 9999,
  },
  bar: {
    position: 'absolute',
    height: '100%',
    width: '40%',
    backgroundColor: theme.colors.teal[6],
    animation: `${slideRight} 1s ease-in-out infinite`,
  },
}))

const MIN_DURATION_MS = 300

interface LinearProgressProps {
  visible: boolean
}

export const LinearProgress: FC<LinearProgressProps> = memo(({ visible }) => {
  const { classes } = useStyles()
  const [show, setShow] = useState(false)
  const startTimeRef = useRef<number>(0)

  useEffect(() => {
    if (visible) {
      startTimeRef.current = Date.now()
      setShow(true)
    } else if (show) {
      const elapsed = Date.now() - startTimeRef.current
      const remaining = MIN_DURATION_MS - elapsed

      if (remaining > 0) {
        const timer = setTimeout(() => setShow(false), remaining)
        return () => clearTimeout(timer)
      }
      setShow(false)
    }
  }, [visible, show])

  if (!show) return null

  return (
    <Box className={classes.root}>
      <Box className={classes.bar} />
    </Box>
  )
})

LinearProgress.displayName = 'LinearProgress'
